import Base from './base'

export default class Crm extends Base {
  listOrganizations(data: object = {}) {
    return this.apiClient.get('crm/organizations', data)
  }

  listOrganizationOptions() {
    return this.apiClient.get('crm/organizations/options')
  }

  showOrganization(id: string, source: 'organization' | 'entity' = 'organization') {
    return this.apiClient.get(`crm/organizations/${id}`, { source })
  }

  updateOrganization(id: string, data: object) {
    return this.apiClient.put(`crm/organizations/${id}`, data)
  }

  organizationFieldOptions(id: string) {
    return this.apiClient.get(`crm/organizations/${id}/field-options`)
  }

  organizationOverviewFeed(id: string) {
    return this.apiClient.get(`crm/organizations/${id}/overview-feed`)
  }

  regenerateOrganizationDossier(id: string) {
    return this.apiClient.post(`crm/organizations/${id}/regenerate-dossier`)
  }

  regenerateOrganizationCatchUp(id: string) {
    return this.apiClient.post(`crm/organizations/${id}/regenerate-catch-up`)
  }

  updateOrganizationDossier(id: string, dossier: string) {
    return this.apiClient.put(`crm/organizations/${id}/dossier`, { dossier })
  }

  listCompanies(data: object = {}) {
    return this.apiClient.get('crm/companies', data)
  }

  listCompanyOptions() {
    return this.apiClient.get('crm/companies/options')
  }

  showCompany(id: string) {
    return this.apiClient.get(`crm/companies/${id}`)
  }

  companyOverviewFeed(id: string) {
    return this.apiClient.get(`crm/companies/${id}/overview-feed`)
  }

  listDeals(companyId: string) {
    return this.apiClient.get(`crm/companies/${companyId}/deals`)
  }

  advanceDeal(id: string, data: object) {
    return this.apiClient.put(`crm/deals/${id}/advance`, data)
  }

  closeDeal(id: string, data: object) {
    return this.apiClient.put(`crm/deals/${id}/close`, data)
  }

  deleteDeal(id: string) {
    return this.apiClient.delete(`crm/deals/${id}`)
  }

  deleteCompany(id: string) {
    return this.apiClient.delete(`crm/companies/${id}`)
  }

  deletePerson(id: string) {
    return this.apiClient.delete(`crm/people/${id}`)
  }

  deleteOrganization(id: string) {
    return this.apiClient.delete(`crm/organizations/${id}`)
  }

  /** `collection` is the route segment: companies, people or organizations. */
  mergeRecord(collection: string, id: string, duplicateId: string) {
    return this.apiClient.post(`crm/${collection}/${id}/merge`, { duplicateId })
  }

  mergePreview(data: { kind: string; id: string; duplicateId: string }) {
    return this.apiClient.get('crm/merge-preview', data)
  }

  updateProspectNextSteps(companyId: string, data: object) {
    return this.apiClient.put(`crm/companies/${companyId}/next-steps`, data)
  }

  checkDuplicates(data: { kind: string; name: string }) {
    return this.apiClient.get('crm/duplicate-check', data)
  }

  createCompany(data: object) {
    return this.apiClient.post('crm/companies', data)
  }

  createPerson(data: object) {
    return this.apiClient.post('crm/people', data)
  }

  createOrganization(data: object) {
    return this.apiClient.post('crm/organizations', data)
  }

  updateCompany(id: string, data: object) {
    return this.apiClient.put(`crm/companies/${id}`, data)
  }

  updateCompanyOwner(data: object) {
    return this.apiClient.put('crm/companies/owner', data)
  }

  updateCompanyRevisit(data: object) {
    return this.apiClient.put('crm/companies/revisit', data)
  }

  createCompanyTeamMember(companyId: string, data: object) {
    return this.apiClient.post(`crm/companies/${companyId}/team`, data)
  }

  updateCompanyTeamMember(companyId: string, id: string, data: object) {
    return this.apiClient.put(`crm/companies/${companyId}/team/${id}`, data)
  }

  deleteCompanyTeamMember(companyId: string, id: string) {
    return this.apiClient.delete(`crm/companies/${companyId}/team/${id}`)
  }

  createCompanyOrganization(companyId: string, data: object) {
    return this.apiClient.post(`crm/companies/${companyId}/organizations`, data)
  }

  updateCompanyOrganization(companyId: string, id: string, data: object) {
    return this.apiClient.put(`crm/companies/${companyId}/organizations/${id}`, data)
  }

  deleteCompanyOrganization(companyId: string, id: string) {
    return this.apiClient.delete(`crm/companies/${companyId}/organizations/${id}`)
  }

  createCapitalRaise(companyId: string, data: object) {
    return this.apiClient.post(`crm/companies/${companyId}/capital-raises`, data)
  }

  updateCapitalRaise(companyId: string, id: string, data: object) {
    return this.apiClient.put(`crm/companies/${companyId}/capital-raises/${id}`, data)
  }

  deleteCapitalRaise(companyId: string, id: string) {
    return this.apiClient.delete(`crm/companies/${companyId}/capital-raises/${id}`)
  }

  updateCapitalRaiseInvestment(companyId: string, investmentId: string, data: object) {
    return this.apiClient.put(`crm/companies/${companyId}/investments/${investmentId}`, data)
  }

  // Developments older than the ones the Overview feed already carries — what the
  // expanded card's "Load more" appends. Addressed by offset, not page.
  listCompanyMajorDevelopments(companyId: string, data: object = {}) {
    return this.apiClient.get(`crm/companies/${companyId}/major-developments`, data)
  }

  createCompanyMajorDevelopment(companyId: string, data: object) {
    return this.apiClient.post(`crm/companies/${companyId}/major-developments`, data)
  }

  updateCompanyMajorDevelopment(companyId: string, id: string, data: object) {
    return this.apiClient.put(`crm/companies/${companyId}/major-developments/${id}`, data)
  }

  deleteCompanyMajorDevelopment(companyId: string, id: string) {
    return this.apiClient.delete(`crm/companies/${companyId}/major-developments/${id}`)
  }

  // Not nested under a record: one interaction can be attached to several, so it
  // is addressed on its own rather than under whichever page created it.
  createLoggedInteraction(data: object) {
    return this.apiClient.post('crm/logged-interactions', data)
  }

  updateLoggedInteraction(id: string, data: object) {
    return this.apiClient.put(`crm/logged-interactions/${id}`, data)
  }

  deleteLoggedInteraction(id: string) {
    return this.apiClient.delete(`crm/logged-interactions/${id}`)
  }

  regenerateCompanyDossier(id: string) {
    return this.apiClient.post(`crm/companies/${id}/regenerate-dossier`)
  }

  regenerateCompanyCatchUp(id: string) {
    return this.apiClient.post(`crm/companies/${id}/regenerate-catch-up`)
  }

  updateCompanyDossier(id: string, dossier: string) {
    return this.apiClient.put(`crm/companies/${id}/dossier`, { dossier })
  }

  listPeople(data: object = {}) {
    return this.apiClient.get('crm/people', data)
  }

  listNetwork(kind: string, id: string, data: object = {}) {
    return this.apiClient.get(`crm/network/${kind}/${id}`, data)
  }

  showPerson(id: string) {
    return this.apiClient.get(`crm/people/${id}`)
  }

  personOverviewFeed(id: string) {
    return this.apiClient.get(`crm/people/${id}/overview-feed`)
  }

  regeneratePersonDossier(id: string) {
    return this.apiClient.post(`crm/people/${id}/regenerate-dossier`)
  }

  updatePersonDossier(id: string, dossier: string) {
    return this.apiClient.put(`crm/people/${id}/dossier`, { dossier })
  }

  regeneratePersonCatchUp(id: string) {
    return this.apiClient.post(`crm/people/${id}/regenerate-catch-up`)
  }

  updatePerson(id: string, data: object) {
    return this.apiClient.put(`crm/people/${id}`, data)
  }

  createPersonAffiliation(personId: string, data: object) {
    return this.apiClient.post(`crm/people/${personId}/affiliations`, data)
  }

  updatePersonAffiliation(personId: string, id: string, data: object) {
    return this.apiClient.put(`crm/people/${personId}/affiliations/${id}`, data)
  }
}
